import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

// Material Imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatBadgeModule } from '@angular/material/badge';

// Shared Components & Enums

import { MatMenuModule } from '@angular/material/menu';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { EmptyListComponent } from '../../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { AutomacaoExtracaoDadosService } from '../../../../../shared/services/apis/batch-extracao-dados/automacao-extracao-dados.service';
import { BatchExtracaoDadosLookupService } from '../../../../../shared/services/apis/batch-extracao-dados/batch-extracao-dados-lookup.service';
import { TipoExtracao } from '../../../../../shared/services/models/batch-extracao-dados/tipo-extracao.model';
import { TipoRecorrencia } from '../../../../../shared/services/models/batch-extracao-dados/tipo-recorrencia.model';
import { BatchExtracaoDadosLookupResponse } from '../../../../../shared/services/responses/batch-extracao-dados/batch-extracao-dados-lookup.response';
import { ListAutomacaoExtracaoDadosResponse } from '../../../../../shared/services/responses/batch-extracao-dados/list-automacao-extracao-dados.response';
import { AutomacaoExtracaoDadosStruct } from '../../../../../shared/services/structs/batch-extracao-dados/automacao-extracao-dados.struct';
import { ReturnStruct } from '../../../../../shared/services/structs/return.struct';
import { UtilService } from '../../../../../shared/services/util.service';
import { HeaderComponent } from '../../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-automacao-extracao-dados-lista',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    EmptyListComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatBadgeModule,
    SelectPesquisavelComponent,
    HeaderComponent,
    LoadingBoxDefaultListComponent
  ],
  templateUrl: './automacao-extracao-dados-lista.component.html',
  styleUrl: './automacao-extracao-dados-lista.component.css',
})
export class AutomacaoExtracaoDadosListaComponent implements OnInit {
  public moduloEnum: ModuloEnum;
  public funcionalidadeEnum: FuncionalidadeEnum;

  public model: FormGroup;
  public _isLoading: boolean = false;

  public _dataSource = new MatTableDataSource<AutomacaoExtracaoDadosStruct>();
  public displayedColumns: string[] = [
    'nomeAutomacao',
    'idTipoExtracao',
    'recorrencia',
    'datetimeUltimaExecucao',
    'datetimeProximaExecucao',
    'status',
    'actions',
  ];

  public listaTipoExtracao: TipoExtracao[] = [];
  public listaTipoRecorrencia: TipoRecorrencia[] = [];
  public preselectedTipoExtracao: number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private automacaoExtracaoDadosService: AutomacaoExtracaoDadosService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private batchExtracaoDadosLookupService: BatchExtracaoDadosLookupService,
    private activatedRoute: ActivatedRoute,
    private utilService: UtilService
  ) {
    this.moduloEnum = ModuloEnum.Home;
    this.funcionalidadeEnum = FuncionalidadeEnum.Home_Ferramentas;
  }

  ngOnInit(): void {
    this.getLookup();
    const params = this.activatedRoute.snapshot.params;
    if (params && params['idTipoExtracao']) {
      this.preselectedTipoExtracao = Number(params['idTipoExtracao']);
    }
    this.model = this.formBuilder.group({
      idTipoExtracao: [this.preselectedTipoExtracao ? [this.preselectedTipoExtracao] : []],
    });
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, ['/home/ferramentas/automacao-extracao-dados/cadastrar']);
  }

  private loadFilters() {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;
    const idTipoExtracao = this.model.get('idTipoExtracao')?.value || [];

    this.automacaoExtracaoDadosService.listar(idTipoExtracao).subscribe({
      next: (response: ListAutomacaoExtracaoDadosResponse) => {
        this._isLoading = false;

        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        this._dataSource.data = response.listAutomacaoExtracaoDados || [];
      },
      error: (error) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message || 'Falha ao carregar automações', AlertType.error);
      }
    });
  }

  public aplicarFiltros(): void {
    this.pesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({
      idTipoExtracao: []
    });
    this.pesquisar();
  }

  public get qtdFiltrosAvancados(): number {
    const formValue = this.model.getRawValue();
    return formValue.idTipoExtracao?.length > 0 ? 1 : 0;
  }

  public get idTipoExtracaoControl(): FormControl {
    return this.model.get('idTipoExtracao') as FormControl;
  }

  public abrirModalExcluir(automacao: AutomacaoExtracaoDadosStruct): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir Automação',
        innerHtml: `<p>Deseja realmente excluir a automação "${automacao.nomeAutomacao}"?</p> <p>Ao confirmar você interrompe permanentemente a extração e envio dos dados configurados e esta ação é irreversível.</p>`,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result && result.confirm) {
          this.excluir(automacao.idAutomacaoExtracaoDados);
        }
      },
    });
  }

  private excluir(idAutomacaoExtracao: number): void {
    this._isLoading = true;
    this.automacaoExtracaoDadosService.delete(idAutomacaoExtracao).subscribe({
      next: (response: ReturnStruct) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', 'Automação excluída com sucesso.', AlertType.success);
        this.pesquisar(); // Recarrega a lista
      },
      error: (error) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      }
    });
  }

  getLookup() {
    this.batchExtracaoDadosLookupService.get().subscribe({
      next: (response: BatchExtracaoDadosLookupResponse) => {
        console.log(response)
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.error);
          return;
        }

        this.listaTipoExtracao = response.listTipoExtracao.filter(x => x.possuiAutomacao === true) || [];
        this.listaTipoRecorrencia = response.listTipoRecorrencia || [];
        this.loadFilters();
        this.pesquisar();
      },
      error: (error) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message || 'Falha ao carregar automações', AlertType.error);
      }
    });
  }
}